/**
 * 自社の広告の枠（作り手が自分のゲームの広告を出す・2026-10-22 money/self-serve-ads-dash）の英語の文言。en.ts が `...enSelfAds` で取り込む。
 * 画面: pages/money/AdsListPage.tsx・AdsNewPage.tsx・AdDetailPage.tsx・pages/StaffAdsReviewPage.tsx・components/SelfAdHostChoice.tsx・AdBanner.tsx。
 *
 * 言い回しの決め事:
 *   - 非エンジニアの作り手に自然な言葉。「campaign」「impression」「CPM」「creative」と言わない → ad / views / per 1,000 views / banner
 *   - 「無料」と書かない（wording.test.ts）。断られた広告はお金がかからない = 「only the AI cost of making the banner」と事実で書く
 *   - お金は「credits」ではなく今の画面と同じ「balance」系の言葉に寄せ、広告に使えるのはどれかをはっきり書く（招待・KUMO からのクレジットは使えない）
 *   - Adsterra の名前は作り手の選択でだけ出す（今のバナー広告のカードが Adsterra の名前を出している = 同じ所の言葉に合わせる）
 *   - 日の区切りは UTC（サーバーの決まり）。数字の画面の棒の下にだけ書く
 */
export const enSelfAds = {
  // ------------------------------------------------------------ 一覧・ナビ
  'ssa.nav': 'Promote an app',
  'ssa.title': 'Promote your apps',
  'ssa.lead': 'Show a banner for one of your published apps inside other {product} apps. You pay only for views: {price} per 1,000.',
  'ssa.new': 'New ad',
  'ssa.noLine': 'No line yet',
  'ssa.allStopped': 'Ads from creators are paused across {product} right now. Nothing is being shown or charged. Your ads will pick up again on their own.',
  'ssa.empty.title': 'Bring more people to your app',
  'ssa.empty.body': 'Pick one of your published apps, let AI make the banner, and choose a daily limit. Your ad shows at the bottom of other creators’ apps, and you pay {price} per 1,000 views.',
  'ssa.off.title': 'Not available yet',
  'ssa.off.body': 'Promoting your apps isn’t open on this server yet.',
  'ssa.row.perDay': '{amount} a day · about {views} views',
  'ssa.row.spent': '{amount} spent',
  'ssa.row.continue': 'Continue',
  'ssa.row.fix': 'Fix and send again',

  // ------------------------------------------------------------ 状態
  'ssa.status.draft': 'Not sent yet',
  'ssa.status.reviewing': 'Being checked',
  'ssa.status.active': 'Showing',
  'ssa.status.paused': 'Paused',
  'ssa.status.rejected': 'Not approved',
  'ssa.status.stopped': 'Stopped by {product}',
  'ssa.status.ended': 'Finished',
  'ssa.why.draft': 'You haven’t sent this ad yet. Finish the steps to send it for a check.',
  'ssa.why.reviewing': 'We’re checking your ad. This usually takes less than a day. Nothing is charged until it starts showing.',
  'ssa.why.active': 'Your ad is showing in other creators’ apps. You’re charged once a day, only for the views it got.',
  'ssa.why.paused_advertiser': 'You paused this ad. Nothing is shown or charged until you resume it.',
  'ssa.why.paused_insufficient_credits': 'Paused because your balance can’t cover today’s limit. Add funds, then resume.',
  'ssa.why.paused_target_changed': 'Paused because you published a new version of the app. We’ll check the ad again, then it starts showing by itself.',
  'ssa.why.paused_advertiser_stopped': 'Paused by {product}. If you think this is a mistake, contact support.',
  'ssa.why.rejected': 'This ad wasn’t approved. Nothing was charged except the AI cost of making the banner. Fix it and send it again.',
  'ssa.why.stopped': '{product} stopped this ad. It can’t be resumed.',
  'ssa.why.ended': 'This ad reached its total limit and finished.',

  // ------------------------------------------------------------ 作る 4 段
  'ssa.new.title': 'New ad',
  'ssa.new.back': 'All ads',
  'ssa.step.what': 'App',
  'ssa.step.banner': 'Banner',
  'ssa.step.budget': 'Budget',
  'ssa.step.review': 'Send',
  'ssa.next': 'Next',
  'ssa.back': 'Back',
  'ssa.optional': '(optional)',
  'ssa.saved': 'Saved.',

  'ssa.what.title': 'Which app do you want to promote?',
  'ssa.what.lead': 'It has to be published and made for everyone (not for kids). People who tap the ad go straight to it.',
  'ssa.what.pick': 'Your apps',
  'ssa.what.noGames': 'You don’t have any apps yet. Publish one first, then come back.',
  'ssa.what.pasteToggle': 'Or paste the app’s address',
  'ssa.what.pasteLabel': 'App address',
  'ssa.what.check': 'Check',
  'ssa.what.checking': 'Checking the app…',
  'ssa.what.ok': 'Published and for everyone. Good to go.',
  'ssa.what.hint': 'Anything the AI should know?',
  'ssa.what.hintPlaceholder': 'e.g. sail through a storm in one minute',
  'ssa.what.make': 'Continue',
  'ssa.what.making': 'Saving…',
  'ssa.what.fixed': 'To promote a different app, start a new ad.',

  'ssa.banner.title': 'Your banner',
  'ssa.banner.lead': 'Make one with AI, or use your own picture. It shows in two sizes: one for phones and one for computers.',
  'ssa.banner.aiTitle': 'Make it with AI',
  'ssa.banner.aiHelp': 'AI draws a banner with your line from your app. The AI cost comes out of your balance.',
  'ssa.banner.aiMake': 'Make with AI',
  'ssa.banner.aiMakeCost': 'Make with AI (about {cost})',
  'ssa.banner.uploadTitle': 'Or use your own picture',
  'ssa.banner.uploadHelp': 'PNG, JPEG or WebP. We cut one picture to fit both sizes (320×50 and 728×90) and keep the middle, so wide pictures work best. Uploading has no AI cost.',
  'ssa.banner.uploadPick': 'Choose a picture',
  'ssa.banner.uploading': 'Uploading…',
  'ssa.banner.uploaded': 'Picture added.',
  'ssa.banner.uploadEach': 'Use a different picture for each size',
  'ssa.banner.uploadPhone': 'Phone picture (320×50)',
  'ssa.banner.uploadComputer': 'Computer picture (728×90)',
  'ssa.banner.uploadNotImage': 'Choose a PNG, JPEG or WebP picture.',
  'ssa.banner.uploadTooBig': 'That picture is too big. Choose one under 15 MB.',
  'ssa.banner.checkNote': 'Every ad is checked by AI and then by our team before it shows. Adult content, gore, drugs, gambling, hate and scams aren’t allowed.',
  'ssa.banner.phone': 'Phone',
  'ssa.banner.computer': 'Computer',
  'ssa.banner.play': 'Play',
  'ssa.banner.loading': 'Loading…',
  'ssa.banner.sample': 'Preview',
  'ssa.banner.none': 'No banner yet',
  'ssa.banner.line': 'Line',
  'ssa.banner.count': '{n}/{max}',
  'ssa.banner.lineHelp': 'Short and clear works best. Leave it empty to use the app’s name.',
  'ssa.banner.saveLine': 'Save line',
  'ssa.banner.again': 'Make another',
  'ssa.banner.againCost': 'Make another (about {cost})',
  'ssa.banner.making': 'Making…',
  'ssa.banner.use': 'Use this banner',

  'ssa.budget.title': 'How much do you want to spend?',
  'ssa.budget.daily': 'Daily limit',
  'ssa.budget.option': '{amount} a day · about {views} views',
  'ssa.budget.dailyHelp': 'We suggest {suggested} to start. Some days use less; you never pay more than this in a day.',
  'ssa.budget.total': 'Total limit',
  'ssa.budget.totalOption': '{amount} in total · about {views} views',
  'ssa.budget.noLimit': 'No total limit',
  'ssa.budget.totalHelpNone': 'The ad keeps going until you pause it or your balance runs out.',
  'ssa.budget.totalHelp': 'The ad finishes by itself when it reaches this amount.',
  'ssa.budget.price': 'Price',
  'ssa.budget.priceValue': '{price} per 1,000 views, tax included',
  'ssa.budget.balance': 'Balance you can use for ads',
  'ssa.budget.lasts': { one: 'enough for about {count} day', other: 'enough for about {count} days' },
  'ssa.budget.which': 'Only money you added with a card, or moved over from your earnings, can pay for ads. Invite credit and credit from {product} can’t.',
  // 売上を見せない間（launchHidden の payouts）の同じ文。「売上から移した分」を言わない
  'ssa.budget.whichLaunch': 'Only money you added with a card can pay for ads. Invite credit and credit from {product} can’t.',
  'ssa.budget.ownGames': 'Your ad never shows in your own apps.',
  'ssa.budget.charged': 'Each day we set aside your daily limit, then charge only for the views you got and return the rest.',
  'ssa.budget.short': 'Your balance is below {amount}, so the ad can’t start yet.',
  'ssa.budget.addFunds': 'Add funds',

  'ssa.review.title': 'Check and send',
  'ssa.review.game': 'App',
  'ssa.review.change': 'Change',
  'ssa.review.nextTitle': 'What happens next',
  'ssa.review.next1': 'AI checks your ad right away, then our team checks it, usually within a day.',
  'ssa.review.next2': 'Once approved, it starts showing in other creators’ apps.',
  'ssa.review.next3': 'You’re charged once a day, only for the views it got. You can pause anytime.',
  'ssa.review.send': 'Send for review',
  'ssa.review.sending': 'Sending…',

  // ------------------------------------------------------------ 数字の画面
  'ssa.detail.all': 'All ads',
  'ssa.detail.sent': 'Sent. We’ll check your ad, usually within a day.',
  'ssa.detail.status': 'Status',
  'ssa.detail.reason': 'Reason:',
  'ssa.detail.pause': 'Pause',
  'ssa.detail.resume': 'Resume',
  'ssa.detail.today': 'Today',
  'ssa.detail.total': 'All time',
  'ssa.detail.week': 'Views, last 7 days',
  'ssa.detail.weekLabel': '{views} views in the last 7 days',
  'ssa.detail.weekNote': 'Days end at midnight UTC.',
  'ssa.detail.where': 'Where it showed',
  'ssa.detail.whereNone': 'Not shown anywhere yet.',
  'ssa.detail.whereNote': 'Last 7 days. One app never gets more than a fifth of your views.',
  'ssa.detail.limits': 'Limits',
  'ssa.detail.changeLimits': 'Change',
  'ssa.detail.saveLimits': 'Save limits',
  'ssa.detail.limitsNote': 'A higher limit applies right away. A lower one starts tomorrow.',
  'ssa.detail.ad': 'Your ad',
  'ssa.detail.goesTo': 'Goes to',
  'ssa.stat.views': 'Views',
  'ssa.stat.clicks': 'Taps',
  'ssa.stat.rate': '{rate} of views',
  'ssa.stat.spent': 'Spent',
  'ssa.stat.spentTodayHint': 'So far. Charged at the end of the day.',
  'ssa.stat.charged': 'Charged so far',
  'ssa.stat.chargedHint': 'Today’s {amount} is added at the end of the day.',
  'ssa.stat.leftToday': 'Left today',
  'ssa.stat.ofDaily': 'of {amount} a day',
  'ssa.stat.leftTotal': 'Left in total',
  'ssa.stat.ofTotal': 'of {amount}',
  'ssa.stat.noLimit': 'No limit',

  // ------------------------------------------------------------ 理由（だめの理由・作り手にも運営にも出る）
  'ssa.reason.gambling': 'Gambling or get-rich promises',
  'ssa.reason.adult': 'Adult or sexual content',
  'ssa.reason.violence': 'Graphic violence or gore',
  'ssa.reason.drugs': 'Alcohol, tobacco or drugs',
  'ssa.reason.deceptive': 'Scams or a misleading look (fake buttons, fake warnings, “you won”)',
  'ssa.reason.impersonation': 'Uses someone else’s characters, logos or name',
  'ssa.reason.politics': 'Politics or religion',
  'ssa.reason.hate': 'Hate or discrimination',
  'ssa.reason.external_link': 'Leads outside {product}',
  'ssa.reason.other': 'Not suitable for ads',
  'ssa.reason.missing_banner': 'No banner',

  // ------------------------------------------------------------ 口のエラー
  'ssa.err.invalid_url': 'That doesn’t look like an app address. It should start with https://',
  'ssa.err.target_not_kumo_game': 'Only apps published on {product} can be promoted (not custom domains or other sites).',
  'ssa.err.target_not_owned': 'You can only promote your own apps.',
  'ssa.err.target_not_public': 'This app isn’t published yet. Publish it first, then come back.',
  'ssa.err.target_not_general': 'Apps made for kids can’t be promoted.',
  'ssa.err.target_suspended': 'Ads are turned off for this app by {product}.',
  'ssa.err.invalid_daily_cap': 'Pick a daily limit from the list.',
  'ssa.err.invalid_total_cap': 'The total limit can’t be lower than the daily limit.',
  'ssa.err.banner_asset_invalid': 'That banner can’t be used. Make another one.',
  'ssa.err.review_rejected': 'Our automatic check found a problem. Fix the banner or line and send it again:',
  'ssa.err.insufficient_ad_credits': 'You don’t have balance that can pay for ads. Add funds with a card, then try again.',
  'ssa.err.invalid_state': 'This ad can’t do that right now. Reload the page to see where it is.',
  'ssa.err.self_ads_stopped': 'Ads from creators are paused across {product} right now. Try again later.',
  'ssa.err.campaign_not_found': 'This ad doesn’t exist, or it isn’t yours.',
  'ssa.err.route_missing': 'This part isn’t ready on this server yet.',
  'ssa.err.suggest_failed': 'The AI couldn’t make the banner. Try again in a moment.',
  'ssa.err.review_unavailable': 'The automatic check isn’t available right now. Try again in a few minutes.',
  'ssa.err.review_limit': 'You’ve sent ads for checking many times today. Try again tomorrow.',
  'ssa.err.banner_type_invalid': 'Use a PNG, JPEG or WebP picture.',
  'ssa.err.banner_too_large': 'That picture is too big for a banner. Try a smaller one.',
  'ssa.err.banner_size_invalid': 'That picture is the wrong shape for this banner size.',
  'ssa.err.banner_animated': 'Moving pictures can’t be used. Use a still picture.',

  // ------------------------------------------------------------ 作り手の「どの広告を出すか」（バナー広告のカード）
  'ssa.host.legend': 'Which ads can show here?',
  'ssa.host.kumo_and_adsterra': 'Ads from {product} creators, then Adsterra',
  'ssa.host.kumo_and_adsterra.help': 'Creators’ ads usually pay you more. When there are none, Adsterra fills the space.',
  'ssa.host.kumo_only': 'Only ads from {product} creators',
  'ssa.host.kumo_only.help': 'When there are none, the space stays empty.',
  'ssa.host.adsterra_only': 'Only Adsterra',
  'ssa.host.adsterra_only.help': 'Other creators’ ads never show on your app.',
  'ssa.host.recommended': 'Recommended',
  'ssa.host.note': 'Your own ads never show here, and only ads for apps made for everyone do. You get 100% of what creators pay for views on your app.',
  'ssa.host.newTitle': 'New: ads from other {product} creators',
  'ssa.host.newBody': 'Other creators can now promote their apps in your banner space. They pay per view, and that money comes to you. You can switch to Adsterra only below.',
  'ssa.host.gotIt': 'Got it',

  // ------------------------------------------------------------ 旗 house の「KUMO の広告を出す / 出さない」（2026-10-24 house-optout-fill）
  // 書き方: 作り手の言葉・何が起きるかだけ（「KUMODeck がやってくれる」とは書かない）・額は書かない（帯とリワードで違う・運営が変えうる）
  'ssa.house.legend': 'Ads from {product}',
  'ssa.house.note': 'Right now, the only ads in your app are short promos for {product}. You get a set amount for each one people see in your app. You choose where they can show.',
  'ssa.house.banner': 'Show {product} ads in your banner space',
  'ssa.house.banner.help': 'A small banner about {product}. When this is off, your banner space stays empty.',
  'ssa.house.rewarded': 'Show {product} videos for rewards',
  'ssa.house.rewarded.help': 'People watch a short video about {product} to the end and get your reward. When this is off, your app is told no video is available, and no reward is given.',

  // ------------------------------------------------------------ 運営の確認（運営だけが見る）
  'ssa.staff.eyebrow': 'Staff',
  'ssa.staff.title': 'Ads to check',
  'ssa.staff.count': { one: '{count} waiting', other: '{count} waiting' },
  'ssa.staff.key': 'Staff key',
  'ssa.staff.keyHelp': 'Kept only in this tab. It’s gone when you close it.',
  'ssa.staff.open': 'Open',
  'ssa.staff.reload': 'Reload',
  'ssa.staff.none': 'Nothing to check right now.',
  'ssa.staff.goesTo': 'Goes to',
  'ssa.staff.age': { one: 'published {count} day ago', other: 'published {count} days ago' },
  'ssa.staff.ai': 'Automatic check',
  'ssa.staff.rules': 'Rules',
  'ssa.staff.aiCheck': 'AI',
  'ssa.staff.aiOff': 'AI check isn’t set up on this server',
  'ssa.staff.verdict.ok': 'OK',
  'ssa.staff.verdict.ng': 'Not OK',
  'ssa.staff.verdict.review': 'Unsure',
  'ssa.staff.advertiser': 'Creator',
  'ssa.staff.topups': { one: '{count} card top-up', other: '{count} card top-ups' },
  'ssa.staff.budget': 'Daily limit',
  'ssa.staff.sent': 'Sent',
  'ssa.staff.approve': 'Approve',
  'ssa.staff.reject': 'Don’t approve',
  'ssa.staff.why': 'Why? (the creator sees this)',
  'ssa.staff.note': 'Note',
  'ssa.staff.noteHelp': '(staff only)',
  'ssa.staff.rejectConfirm': 'Don’t approve'
} as const;
